import { Box, CircularProgress } from '@mui/material';
import React, { Component } from 'react';
import Document from '../../models/Document';
import FundOpportunity from '../../models/FundOpportunity';
import Investor from '../../models/Investor';
import { downloadDoc, downloadPublishedPDF } from '../../services/DocUtils';
import { StyledDocumentLink } from '../styles/StyledLink';
import StyledSection from '../styles/StyledSection';
import { StyledFilterButton, StyledHeading } from '../styles/styles';

const ALL_CATEGORIES = '__all__';
const UNCATEGORISED = 'General';

// Same pill as the EOI section's filters (StyledFilterButton), a size down.
const CATEGORY_PILL_SX = { padding: '2px 12px', fontSize: '0.8125rem', minWidth: 0 } as const;

interface FundInformationProps {
  investor: Investor;
  fundOpportunity: FundOpportunity;
  loadDocs: () => Promise<Document[]>;
}

interface FundInformationState {
  documents?: Document[];
  loading: boolean;
}

class Documents extends Component<{ documents?: Document[] | null }, { selectedCategory: string }> {
  state = { selectedCategory: ALL_CATEGORIES };

  private categoryNames(documents: Document[]): string[] {
    const names = new Set<string>();
    documents.forEach((doc) => names.add(doc.categoryName ?? UNCATEGORISED));
    return [...names];
  }

  render() {
    const documents = this.props.documents ?? [];
    const names = this.categoryNames(documents);
    const showPills = names.length > 1;
    const { selectedCategory } = this.state;

    const visible =
      showPills && selectedCategory !== ALL_CATEGORIES
        ? documents.filter((doc) => (doc.categoryName ?? UNCATEGORISED) === selectedCategory)
        : documents;

    return (
      <>
        {showPills && (
          <Box sx={{ mt: '1.25rem', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <StyledFilterButton
              active={selectedCategory === ALL_CATEGORIES}
              onClick={() => this.setState({ selectedCategory: ALL_CATEGORIES })}
              sx={CATEGORY_PILL_SX}
            >
              All
            </StyledFilterButton>
            {names.map((name) => (
              <StyledFilterButton
                key={name}
                active={selectedCategory === name}
                onClick={() => this.setState({ selectedCategory: name })}
                sx={CATEGORY_PILL_SX}
              >
                {name}
              </StyledFilterButton>
            ))}
          </Box>
        )}
        {visible.map((doc) => (
          <Box key={doc.id} sx={{ mt: '0.6875rem' }}>
            <StyledDocumentLink onClick={() => downloadDoc(doc)}>{doc.title}</StyledDocumentLink>
          </Box>
        ))}
      </>
    );
  }
}
class FundInformation extends Component<FundInformationProps, FundInformationState> {
  constructor(props: FundInformationProps) {
    super(props);

    this.state = {
      documents: [],
      loading: true,
    };
  }

  componentDidMount() {
    if (this.state.loading) {
      this.props
        .loadDocs()
        .then((documents) => {
          this.setState({ documents, loading: false });
        })
        .catch((error) => {
          console.error('Error retrieving documents:', error);
          this.setState({ documents: [], loading: false });
        });
    }
  }

  render() {
    const { fundOpportunity } = this.props;

    return (
      <StyledSection tone="tone1" sx={{ mt: '1.875rem' }}>
        <StyledHeading>Fund Information</StyledHeading>
        <StyledHeading headerVariant="heading2">Fund Document Library</StyledHeading>
        <StyledDocumentLink onClick={() => downloadPublishedPDF(fundOpportunity)}>
          Expression of Interest
        </StyledDocumentLink>
        {this.state.loading ? (
          <Box>
            <CircularProgress />
          </Box>
        ) : (
          <Documents documents={this.state.documents} />
        )}
      </StyledSection>
    );
  }
}

export default FundInformation;
